import React, { useState, useEffect } from "react";
import { DatePicker, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../core/data/redux/store";
import { fetchAllGoaltype } from "../data/redux/performance/goaltype/goaltypeAction";
import {
  createGoallist,
  UpdateGoallist,
  deleteGoallist,
} from "../../core/data/redux/performance/goallist/goallistAction";
import { fetchAllGoal } from "../data/redux/performance/goal/goalAction";
import Select from "react-select";
import moment from "moment";
import dayjs from "dayjs";


interface AssignGoalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: any;
  goaldata?: any;
}

const StarRating = ({
  rating,
  onRate,
}: {
  rating: number;
  onRate: (rating: number) => void;
}) => {
  const handleClick = (star: number, isHalf: boolean) => {
    const newRating = isHalf ? star - 0.5 : star;
    onRate(newRating);
  };

  return (
    <div style={{ display: "flex", gap: "4px" }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <div
          key={star}
          style={{ position: "relative", fontSize: "24px", cursor: "pointer" }}
        >
          <span
            style={{
              position: "absolute",
              width: "50%",
              overflow: "hidden",
              color: rating >= star - 0.5 ? "gold" : "gray",
            }}
            onClick={() => handleClick(star, true)}
          >
            ★
          </span>

          <span
            style={{
              color: rating >= star ? "gold" : "gray",
            }}
            onClick={() => handleClick(star, false)}
          >
            ★
          </span>
        </div>
      ))}
    </div>
  );
};

const AssignGoal: React.FC<AssignGoalProps> = ({
  isOpen,
  onClose,
  employee,
  goaldata,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { goaltype } = useSelector((state: any) => state.performance.goaltype);
  const { goal } = useSelector((state: any) => state.performance.goal);
  const [formData, setFormData] = useState<any>({
    goal: "",
    subject: "",
    achievement: "",
    startDate: "",
    endDate: "",
    description: "",
    status: "",
    hrRating: "",
    hodRating: "",
    roRating: "",
    employeeId: employee?.EmployeeID,
    ...(goaldata ? { modifiedBy: user?.UserId } : { createdBy: user?.UserId }),
  });

  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    dispatch(fetchAllGoal());
  }, [dispatch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (selectedOption: any, action: any) => {
    setFormData({
      ...formData,
      [action.name]: selectedOption?.value || "",
    });
  };

  useEffect(() => {
    if (goaldata) {
      setFormData({
        goal: goaldata.goal || "",
        subject: goaldata.subject || "",
        achievement: goaldata.achievement || "",
        startDate: goaldata.StartDate
          ? moment(goaldata.StartDate).format("YYYY-MM-DD")
          : "",
        endDate: goaldata.EndDate
          ? moment(goaldata.EndDate).format("YYYY-MM-DD")
          : "",
        description: goaldata.description || "",
        employeeId: employee?.EmployeeID || "",
        status: goaldata.Status || "active",
        modifiedBy: user?.UserId,
        hrRating: goaldata.hr_rating,
        hodRating: goaldata.hod_rating,
        roRating: goaldata.manager_rating,
      });
      setErrors({});
    }
  }, [goaldata]);

  const handleDateChange = (date: any, name: string) => {
    setFormData({ ...formData, [name]: date ? date.format("YYYY-MM-DD") : "" });
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    if (goaldata) {
      dispatch(
        UpdateGoallist({ goallistId: goaldata.id, data: formData })
      ).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          message.success(res.payload.message);
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          message.warning(res.payload.error);
        }
      });
    } else {
      dispatch(createGoallist(formData))
        .then((res) => {
          if (res.meta.requestStatus === "fulfilled") {
            message.success(res.payload.message);
            setFormData({
              goal: [],
              subject: "",
              achievement: "",
              startDate: "",
              endDate: "",
              description: "",
              employeeId: employee?.EmployeeID,
              createdBy: user?.UserId,
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            message.warning(res.payload.message);
          }
        })
        .catch((error: any) => {
          console.error("Error:", error);
        });
    }
  };

  const handleRatingChange = (
    newRating: number,
    key: "hrRating" | "hodRating" | "roRating"
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      [key]: newRating,
    }));
  };

  const validateForm = () => {
    let formIsValid = true;

    let newErrors: any = {
      goal: "",
      subject: "",
      achievement: "",
      startDate: "",
      endDate: "",
      description: "",
      status: "",
      hrRating: "",
      hodRating: "",
      roRating: "",
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0); // clear time for accurate date comparison

    // Goal
    if (!formData.goal) {
      formIsValid = false;
      newErrors.goal = "Goal is required.";
    }

    // Subject
    if (!formData.subject) {
      formIsValid = false;
      newErrors.subject = "Subject is required.";
    }

    // Achievement
    if (!formData.achievement) {
      formIsValid = false;
      newErrors.achievement = "Achievement is required.";
    }

    // Start Date
    if (!formData.startDate) {
      formIsValid = false;
      newErrors.startDate = "Start Date is required.";
    } else {
      const startDate = new Date(formData.startDate);
      if (startDate < today) {
        formIsValid = false;
        newErrors.startDate = "Start Date cannot be in the past.";
      }
    }

    // End Date
    if (!formData.endDate) {
      formIsValid = false;
      newErrors.endDate = "End Date is required.";
    } else {
      const endDate = new Date(formData.endDate);
      const startDate = new Date(formData.startDate);
      if (endDate < today) {
        formIsValid = false;
        newErrors.endDate = "End Date cannot be in the past.";
      }
      if (formData.startDate && endDate < startDate) {
        formIsValid = false;
        newErrors.endDate = "End Date cannot be before Start Date.";
      }
    }



    // Status
    if (!formData.status || formData.status === "Select") {
      formIsValid = false;
      newErrors.status = "Status is required.";
    }

    // Ratings (0 to 10)
    if (formData.hrRating < 0 || formData.hrRating > 10) {
      formIsValid = false;
      newErrors.hrRating = "HR Rating must be between 0 and 10.";
    }

    if (formData.hodRating < 0 || formData.hodRating > 10) {
      formIsValid = false;
      newErrors.hodRating = "HOD Rating must be between 0 and 10.";
    }

    if (formData.roRating < 0 || formData.roRating > 10) {
      formIsValid = false;
      newErrors.roRating = "RO Rating must be between 0 and 10.";
    }

    setErrors(newErrors);
    return formIsValid;
  };

  useEffect(() => {
    setFormData((prevData: any) => ({
      ...prevData,
      employeeId: employee?.EmployeeID || "",
    }));
  }, [employee]);

  const formattedGoal = goal.map((emp: any) => ({
    value: emp.id,
    label: emp.name,
  }));
  const statusOptions = [
    { value: "Select", label: "Select" },
    { value: "Approved", label: "Approved" },
    { value: "Pending", label: "Pending" },
    { value: "Hold", label: "Hold" },
  ];

  if (!isOpen) return null;
  const handleClose = () => {
    setFormData({
      goal: "",
      subject: "",
      achievement: "",
      startDate: "",
      endDate: "",
      description: "",
      status: "",
      hrRating: 0,
      hodRating: 0,
      roRating: 0,
      employeeId: employee?.EmployeeID,
      ...(goaldata
        ? { modifiedBy: user?.UserId }
        : { createdBy: user?.UserId }),
    });
    onClose();
    setErrors({});
  };

  return (
    <div className={`offcanvas offcanvas-end show `} tabIndex={-1}>
      <div
        className="offcanvas-header"
        style={{
          width: "35vw",
          background: "#fff",
          boxShadow: "-3px 0px 10px rgba(0,0,0,0.2)",
        }}
      >
        <h4 className="offcanvas-title">
          {goaldata ? "Update Assign Goal" : "Assign Goal"}
        </h4>
        <button
          type="button"
          className="btn-close"
          onClick={handleClose}
        ></button>
      </div>
      <div className="offcanvas-body">
        <form>
          {/* Goal Type */}
          <div className="mb-3">
            <label className="form-label">
              Goal <span className="text-danger"> *</span>
            </label>
            <Select
              name="goal"
              className="select"
              options={formattedGoal}
              value={
                formattedGoal.find((opt: any) => opt.value === formData.goal) ||
                null
              }
              onChange={handleSelectChange}
            />
            {errors.goal && <div style={{ color: "red" }}>{errors.goal}</div>}
          </div>

          {/* Subject */}
          <div className="mb-3">
            <label className="form-label">
              Subject <span className="text-danger"> *</span>
            </label>
            <input
              type="text"
              name="subject"
              className="form-control"
              value={formData.subject}
              onChange={handleChange}
            />
            {errors.subject && (
              <div style={{ color: "red" }}>{errors.subject}</div>
            )}
          </div>

          
          <div className="mb-3">
            <label className="form-label">
              Target Achievement <span className="text-danger"> *</span>
            </label>
            <input
              type="text"
              name="achievement"
              className="form-control"
              value={formData.achievement}
              onChange={handleChange}
            />
            {errors.achievement && (
              <div style={{ color: "red" }}>{errors.achievement}</div>
            )}
          </div>

        
          <div className="mb-3">
            <label className="form-label">
              Start Date <span className="text-danger">*</span>
            </label>
            <DatePicker
              className="form-control"
              format="YYYY-MM-DD"
              value={formData.startDate ? dayjs(formData.startDate, "YYYY-MM-DD") : null}
              onChange={(date) => handleDateChange(date, "startDate")}
            />
            {errors.startDate && (
              <div style={{ color: "red" }}>{errors.startDate}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">
              End Date <span className="text-danger">*</span>
            </label>
            <DatePicker
              className="form-control"
              format="YYYY-MM-DD"
              value={formData.endDate ? dayjs(formData.endDate, "YYYY-MM-DD") : null}
              onChange={(date) => handleDateChange(date, "endDate")}
            />
            {errors.endDate && (
              <div style={{ color: "red" }}>{errors.endDate}</div>
            )}
          </div>

          {/* Description */}
          <div className="mb-3">
            <label className="form-label">
              Description
            </label>
            <textarea
              className="form-control"
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
            {errors.description && (
              <div style={{ color: "red" }}>{errors.description}</div>
            )}
          </div>

          {/* Status */}
          <div className="mb-3">
            <label className="form-label">
              Status <span className="text-danger"> *</span>
            </label>
            <Select
              name="status"
              className="select"
              options={statusOptions}
              value={statusOptions.find((opt) => opt.value === formData.status)}
              onChange={(selectedOption: any) =>
                setFormData({ ...formData, status: selectedOption.value })
              }
            />
            {errors.status && (
              <div style={{ color: "red" }}>{errors.status}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">
              HR Rating <span className="text-danger"> *</span>
            </label>
            <StarRating
              rating={formData.hrRating}
              onRate={(newRating) => handleRatingChange(newRating, "hrRating")}
            />
            {errors.hrRating && (
              <div style={{ color: "red" }}>{errors.hrRating}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">
              HOD Rating <span className="text-danger"> *</span>
            </label>
            <StarRating
              rating={formData.hodRating}
              onRate={(newRating) => handleRatingChange(newRating, "hodRating")}
            />
            {errors.hodRating && (
              <div style={{ color: "red" }}>{errors.hodRating}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">
              Manager Rating <span className="text-danger"> *</span>
            </label>
            <StarRating
              rating={formData.roRating}
              onRate={(newRating) => handleRatingChange(newRating, "roRating")}
            />
            {errors.roRating && (
              <div style={{ color: "red" }}>{errors.roRating}</div>
            )}
          </div>

          {/* Footer */}
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {goaldata ? "Update Goal" : "Create Goal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignGoal;
